import React, { memo, useMemo } from 'react';
import { ArrowCircleRightOutlined, DirectionsBoatFilled, ExpandLessOutlined, ExpandMoreOutlined, Warning } from '@mui/icons-material';
import {
  Badge,
  Chip,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  Typography,
} from '@mui/material';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import { useTheme } from '@mui/material/styles';
import { Box } from '@mui/system';
import moment from 'moment-timezone';
import { useSafetyPredictionServiceGetBerthsSafetyWarningsByDate } from '@/api/ui/queries';
import { BerthSafetyWarningDetails, SafetyPredictionTrigger } from '@/api/ui/requests';
import LoadingIndicator from '@/components/feedback/LoadingIndicator';
import useToBerthSafetyNavigate from '@/features/SafetyPrediction/BerthDashboard/useToBerthSafetyNavigate';
import { BerthMarkerBlackSvg } from '@/features/SafetyPrediction/Icons';
import useOrganisation from '@/hooks/useOrganisation';
import { DATE_FORMAT, DATE_TIME_ZONE_FORMAT, SafetyPredictionTriggerStaticValues } from '@/types';

type DailyWarningsDialogProps = {
  day: moment.Moment;
  onClose: () => void;
  selectedBerthIds?: string[] | undefined;
  withAisVesselsOnly?: boolean;
  selectedTrigger?: SafetyPredictionTrigger | undefined;
};

type BerthSafetyWarningDetailsProps = {
  berthId: string;
  berthName: string;
  vesselWarnings: BerthSafetyWarningDetails[];
  warningSummaryByColor: Map<
    string,
    {
      thresholdPercentage: number;
      numberOfWarnings: number;
    }
  >;
};

const timeWithZone = (time: string, timezones?: string[]) => moment.tz(time, timezones?.at(0) ?? moment.tz.guess(true));

const warningsByBerth = (warnings: BerthSafetyWarningDetails[], defaultColor: string) =>
  warnings.reduce((acc, warning) => {
    const { berthId, berthName, threshold } = warning;
    const collectedWarning = acc?.get(berthId) ?? {
      berthId,
      berthName,
      vesselWarnings: [],
      warningSummaryByColor: new Map(),
    };
    collectedWarning.vesselWarnings.push(warning);

    const thresholdColor = threshold?.color ?? defaultColor;
    const warningSummary = collectedWarning.warningSummaryByColor.get(thresholdColor) ?? {
      thresholdPercentage: threshold?.percentage,
      numberOfWarnings: 0,
    };

    acc.set(berthId, {
      ...collectedWarning,
      warningSummaryByColor: collectedWarning.warningSummaryByColor.set(thresholdColor, {
        ...warningSummary,
        numberOfWarnings: warningSummary.numberOfWarnings + 1,
      }),
    });
    return acc;
  }, new Map<string, BerthSafetyWarningDetailsProps>());

const DailyBerthWarningsDialog = ({ day, selectedBerthIds, withAisVesselsOnly, onClose, selectedTrigger }: DailyWarningsDialogProps) => {
  const { selectedOrganisationId } = useOrganisation();
  const theme = useTheme();
  const date = day.format(DATE_FORMAT);
  const { isLoading, data: warnings } = useSafetyPredictionServiceGetBerthsSafetyWarningsByDate(
    {
      xSelectedOrganisationId: selectedOrganisationId,
      berthIds: selectedBerthIds,
      trigger: selectedTrigger,
      withAisVesselsOnly,
      date,
    },
    [selectedOrganisationId, selectedBerthIds, date, withAisVesselsOnly, selectedTrigger],
    {
      refetchOnWindowFocus: false,
    }
  );

  const warningsByBerths = useMemo(
    () => Array.from(warningsByBerth(warnings ?? [], theme.palette.below_lower_limit?.main).values()),
    [warnings, theme]
  );

  const WarningByBerthRows = () => (
    <>
      {warningsByBerths.map((warningsByBerth) => {
        const warnings = warningsByBerth.vesselWarnings.sort((a, b) => {
          const timestampCompare = moment(a.predictionTimestamp).diff(b.predictionTimestamp, 'minutes');
          if (timestampCompare !== 0) return timestampCompare;
          return b.percentage - a.percentage;
        });
        return <WarningsPerBerthRow key={warningsByBerth.berthId} vesselWarnings={warnings} {...warningsByBerth} />;
      })}
    </>
  );

  return (
    <Dialog open={true} onClose={onClose} maxWidth={false}>
      <DialogTitle>
        <Typography variant={'h3'}>Safety levels exceeded on {day.format('MMM D')}</Typography>
      </DialogTitle>
      <DialogContent sx={{ maxWidth: 'fit-content', scrollbarWidth: 'none', px: 0 }}>
        {isLoading ? (
          <Box width={1000} display={'flex'} justifyContent={'center'}>
            <LoadingIndicator message={'Retrieving safety warnings...'} />
          </Box>
        ) : (
          <TableContainer component={Box} sx={{ width: 1000, overflowX: 'initial' }}>
            <Table size={'small'} stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell align={'right'}>
                    <BerthMarkerBlackSvg />
                  </TableCell>
                  <TableCell align={'left'}>
                    <Typography variant={'headerLarge'}>Berth</Typography>
                  </TableCell>
                  <TableCell align={'right'}>
                    <Typography variant={'headerLarge'}>Affected Vessels</Typography>
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody sx={{ td: { borderBottom: 'unset' } }}>
                <WarningByBerthRows />
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>
  );
};

const WarningSummaryBadges = ({
  warningSummaryByColor,
}: {
  warningSummaryByColor: Map<
    string,
    {
      thresholdPercentage: number;
      numberOfWarnings: number;
    }
  >;
}) => {
  const orderedWarnings = Array.from(warningSummaryByColor.entries(), ([color, { thresholdPercentage, numberOfWarnings }]) => ({
    color,
    thresholdPercentage,
    numberOfWarnings,
  })).sort((a, b) => b.thresholdPercentage - a.thresholdPercentage);

  return (
    <Stack direction={'row-reverse'} spacing={2}>
      {orderedWarnings.map(({ color, numberOfWarnings }) => (
        <Badge
          key={color}
          badgeContent={
            <Chip
              sx={{
                backgroundColor: color,
                color: 'white',
                '> span': { p: 0 },
                height: 18,
                width: 18,
              }}
              label={numberOfWarnings}
            />
          }
        >
          <DirectionsBoatFilled sx={{ width: 24, height: 24 }} />
        </Badge>
      ))}
    </Stack>
  );
};

const WarningsPerBerthRow = ({ berthId, berthName, vesselWarnings, warningSummaryByColor }: BerthSafetyWarningDetailsProps) => {
  const { navigateToBerthSafety } = useToBerthSafetyNavigate();
  const theme = useTheme();
  const [open, setOpen] = React.useState(false);

  return (
    <>
      <TableRow key={berthId}>
        <TableCell>
          <Typography variant={'h6'} align={'right'}>
            {berthId}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant={'header'} align={'left'}>
            {berthName}
          </Typography>
        </TableCell>
        <TableCell>
          <WarningSummaryBadges warningSummaryByColor={warningSummaryByColor} />
        </TableCell>
        <TableCell align={'right'}>
          <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
            {open ? <ExpandLessOutlined /> : <ExpandMoreOutlined />}
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow key={`warning-${berthId}`}>
        <TableCell colSpan={4} sx={{ p: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <TableContainer component={Box} sx={{ bgcolor: theme.palette.grey[100], p: 1 }}>
              <Table size={'small'}>
                <TableHead>
                  <TableRow>
                    <TableCell align={'center'}>
                      <DirectionsBoatFilled sx={{ height: 28, width: 28 }} />
                    </TableCell>
                    <TableCell>Vessel</TableCell>
                    <TableCell>Max %. Threshold</TableCell>
                    <TableCell align={'left'}>Triggered by</TableCell>
                    <TableCell align={'center'}>Vessel Mooring</TableCell>
                    <TableCell align={'center'}>Warning At</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {vesselWarnings?.map((row) => (
                    <TableRow
                      key={`${row.predictionTimestamp}-${row.berthId}-${row.dmaCaseId}-${row.vesselMmsi}-${row.percentage}`}
                      sx={{ td: { borderBottom: 'unset' } }}
                    >
                      <TableCell align="center">
                        <Warning sx={{ color: row.threshold?.color }} />
                      </TableCell>
                      <TableCell align="left">
                        {row.vesselName ? (
                          <>
                            <Typography variant={'h6'}>{row.vesselName}</Typography>
                            <Typography variant={'h6'}>{row.vesselClass}</Typography>
                          </>
                        ) : (
                          <Typography variant={'h6'}>{row.vesselClass}</Typography>
                        )}
                      </TableCell>
                      <TableCell align="left">
                        {row.percentage.toFixed(1)}% {row.threshold?.abbr}
                      </TableCell>
                      <TableCell align="left">{SafetyPredictionTriggerStaticValues[row.trigger]?.name}</TableCell>
                      <TableCell align="center">
                        {row.isOperationalVessel ? (
                          <Stack spacing={0.3}>
                            {row.arrivedAt && (
                              <Chip label={timeWithZone(row.arrivedAt, row.berthTimezones).format(DATE_TIME_ZONE_FORMAT)} />
                            )}
                            {row.departedAt && (
                              <Chip label={timeWithZone(row.departedAt, row.berthTimezones).format(DATE_TIME_ZONE_FORMAT)} />
                            )}
                          </Stack>
                        ) : (
                          'Simulated vessel'
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={timeWithZone(row.predictionTimestamp, row.berthTimezones).format(DATE_TIME_ZONE_FORMAT)} />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          aria-label="navigate to safety prediction"
                          onClick={() =>
                            navigateToBerthSafety({
                              berthId: row.berthId,
                              loadingCondition: row.loadingCondition,
                              trigger: row.trigger,
                              dmaCaseId: row.dmaCaseId,
                              isOperationalVessel: row.isOperationalVessel,
                              vesselDisposition: row.vesselDisposition,
                              requestTimestamp: timeWithZone(row.predictionTimestamp, row.berthTimezones).startOf('day'),
                              vesselMmsi: row.vesselMmsi,
                              days: 1,
                            })
                          }
                        >
                          <ArrowCircleRightOutlined />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export default memo(DailyBerthWarningsDialog);
